import * as React from "react";
import { useSearchParams } from "react-router-dom";

type ID = string;

function readIds(searchParams: URLSearchParams, key: string): ID[] {
  const repeated = searchParams
    .getAll(key)
    .map((value) => value.trim())
    .filter(Boolean);

  const values =
    repeated.length === 1 && repeated[0].includes(",")
      ? repeated[0].split(",")
      : repeated;

  return Array.from(
    new Set(values.map((value) => value.trim()).filter(Boolean))
  );
}

function createSignature(ids: ID[]) {
  return JSON.stringify([...ids].sort());
}

export function useSyncSearchParams(
  key: string,
  ids: ID[],
  setIds: (ids: ID[]) => void,
  getAllOptions?: () => Promise<{ id: ID }[]>,
  paused = false
) {
  const [searchParams, setSearchParams] = useSearchParams();

  const suppressNextWriteRef = React.useRef(false);

  const idsRef = React.useRef(ids);
  idsRef.current = ids;

  const fromUrl = readIds(searchParams, key);

  const fromUrlRef = React.useRef(fromUrl);
  fromUrlRef.current = fromUrl;

  const urlSignature = createSignature(fromUrl);
  const stateSignature = createSignature(ids);

  // URL -> state
  React.useEffect(() => {
    if (paused) return;

    const nextFromUrl = fromUrlRef.current;

    if (createSignature(idsRef.current) === createSignature(nextFromUrl)) {
      return;
    }

    suppressNextWriteRef.current = true;

    let cancelled = false;

    if (getAllOptions) {
      void getAllOptions().then((allOptions) => {
        if (cancelled) return;

        const validIds = new Set(allOptions.map((option) => String(option.id)));

        setIds(nextFromUrl.filter((id) => validIds.has(String(id))));
      });
    } else {
      setIds(nextFromUrl);
    }

    return () => {
      cancelled = true;
    };
  }, [key, paused, urlSignature, setIds, getAllOptions]);

  // state -> URL
  React.useEffect(() => {
    if (paused) return;

    if (suppressNextWriteRef.current) {
      suppressNextWriteRef.current = false;
      return;
    }

    setSearchParams(
      (previous) => {
        const currentFromUrl = readIds(previous, key);
        const nextIds = idsRef.current.filter(Boolean);

        if (createSignature(currentFromUrl) === createSignature(nextIds)) {
          return previous;
        }

        const next = new URLSearchParams(previous);

        next.delete(key);

        for (const id of nextIds) {
          next.append(key, id);
        }

        return next;
      },
      { replace: true }
    );
  }, [key, paused, stateSignature, setSearchParams]);
}
